import * as React from 'react'
import { useMemo } from 'react'

import type { IPatient } from '@app/domain/patient/patient.interface'
import type { IPagination } from '@app/lib/pagination'
import type { ISorting } from '@app/lib/sorting'

import {
  EmptyPatientList,
  PatientListToolbar,
  PopulatedPatientList,
} from './patient-list'
import { ColumnHeader } from './patient-list/patient-list-header'
import { PatientActionItem } from './patient-list/patient-list.interface'

interface IFilters {
  birthSexFilter?: string[]
  onBirthSexFilterUpdated: (birthSex?: string[]) => void
  dateOfBirthFilter?: string
  onDateOfBirthFilterUpdated: (dateOfBirthFilter?: string) => void
  mrnFilter?: string
  onMrnFilterUpdated: (mrnFilter?: string) => void
  nameFilter?: string
  onNameFilterUpdated: (nameFilter?: string) => void
  deliverableFilter?: boolean
  onDeliverableFilterUpdated?: (deliverableFilter?: boolean) => void
}

interface Props {
  buildActions?: (patient: IPatient) => PatientActionItem[]
  buildColumns?: (patient: IPatient) => React.ReactElement[]
  columnHeaders?: ColumnHeader[]
  filters: IFilters
  onActionSelected?: (key: string, patient: IPatient) => void
  onPatientSelected?: (patient: IPatient) => void
  onPrimaryAction?: () => void
  pagination: IPagination
  patients: IPatient[]
  primaryActionLabel?: string
  onRefresh: () => void
  selectedPatients?: IPatient[]
  onClearAllFilters?: () => void
  sorting: ISorting
}

export const PatientListComponent = ({
  buildActions,
  buildColumns,
  columnHeaders,
  filters,
  onActionSelected,
  onPatientSelected,
  onPrimaryAction,
  onRefresh,
  onClearAllFilters,
  patients,
  primaryActionLabel,
  pagination,
  selectedPatients,
  sorting,
}: Props) => {
  const {
    birthSexFilter,
    onBirthSexFilterUpdated,
    dateOfBirthFilter,
    onDateOfBirthFilterUpdated,
    mrnFilter,
    onMrnFilterUpdated,
    nameFilter,
    onNameFilterUpdated,
    deliverableFilter,
    onDeliverableFilterUpdated,
  } = filters

  const filtersInUse = useMemo(
    () =>
      filters.birthSexFilter !== undefined ||
      filters.dateOfBirthFilter !== undefined ||
      filters.deliverableFilter !== undefined ||
      filters.mrnFilter !== undefined ||
      filters.nameFilter !== undefined,
    [
      filters.birthSexFilter,
      filters.dateOfBirthFilter,
      filters.deliverableFilter,
      filters.mrnFilter,
      filters.nameFilter,
    ],
  )
  return (
    <>
      <PatientListToolbar
        birthSex={birthSexFilter}
        onBirthSexUpdated={onBirthSexFilterUpdated}
        dateOfBirth={dateOfBirthFilter}
        onDateOfBirthUpdated={onDateOfBirthFilterUpdated}
        mrnFilter={mrnFilter}
        onMrnFilterUpdated={onMrnFilterUpdated}
        nameFilter={nameFilter}
        deliverableFilter={deliverableFilter}
        onNameFilterUpdated={onNameFilterUpdated}
        onDeliverableFilterUpdated={onDeliverableFilterUpdated}
        onPrimaryAction={onPrimaryAction}
        onClearAllFilters={onClearAllFilters}
        onRefresh={onRefresh}
        primaryActionLabel={primaryActionLabel}
        pagination={pagination}
      />

      {patients.length > 0 && (
        <PopulatedPatientList
          buildActions={buildActions}
          buildColumns={buildColumns}
          columnHeaders={columnHeaders}
          onActionSelected={onActionSelected}
          onPatientSelected={onPatientSelected}
          pagination={pagination}
          patients={patients}
          selectedPatients={selectedPatients}
          sorting={sorting}
        />
      )}

      {patients.length === 0 && (
        <EmptyPatientList
          filtersInUse={filtersInUse}
          sorting={sorting}
          onClearAllFilters={onClearAllFilters}
        />
      )}
    </>
  )
}
